import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DEFAULT_LIBRARY_FILTERS,
  isSameLibraryFilterState,
  LibraryFilterState,
  parseLibraryFilterParams,
  serializeLibraryFilterParams
} from './library.filters'

interface WriteOptions {
  replace?: boolean
}

/* The query string is the only store for the filter state. This hook does not copy it into
 * component state, so the two values cannot differ. */
export const useLibraryFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(() => parseLibraryFilterParams(searchParams), [searchParams])

  /* react-router 6.3 has no functional setter, so this function reads the current params for
   * each write. Two filter changes need one call. */
  const write = useCallback(
    (next: LibraryFilterState, { replace = false }: WriteOptions = {}) => {
      const current = parseLibraryFilterParams(searchParams)
      /* Without this check, a repeat write adds a history entry that changes nothing. */
      if (isSameLibraryFilterState(next, current)) return

      setSearchParams(serializeLibraryFilterParams(next, searchParams), { replace })
    },
    [searchParams, setSearchParams]
  )

  const setFilter = useCallback(
    <K extends keyof LibraryFilterState>(key: K, value: LibraryFilterState[K], options?: WriteOptions) => {
      write({ ...parseLibraryFilterParams(searchParams), [key]: value }, options)
    },
    [searchParams, write]
  )

  const resetFilter = useCallback(
    <K extends keyof LibraryFilterState>(key: K) => {
      setFilter(key, DEFAULT_LIBRARY_FILTERS[key])
    },
    [setFilter]
  )

  const clearFilters = useCallback(() => {
    write(DEFAULT_LIBRARY_FILTERS)
  }, [write])

  return { filters, setFilter, resetFilter, clearFilters, write }
}
